import PermanentError from '@/utils/errors/PermanentError'

export class FoodGrubhubStoreLevelError extends PermanentError {}

type FoodGrubhubProductLevelErrorData = {
  productId: string
  error: string
}[]

export class FoodGrubhubProductLevelError extends PermanentError {
  data: FoodGrubhubProductLevelErrorData
  constructor(data: FoodGrubhubProductLevelErrorData) {
    super()
    this.data = data
  }
}

export const GRUBHUB_ERROR_MESSAGE_KEYS: Record<string, string> = {
  'address_invalid.payment_id':
    "Sorry, the address you provided isn't valid. Please double-check and try again.",
  'address_outside_pickup_radius':
    'Sorry, your address is outside the allowed pickup radius for this restaurant. Try a closer location or choose delivery instead.',
  'cart_contains_prohibited_items':
    "Your cart has items that aren't allowed with this promo code. Remove them to apply the code.",
  'restricted_merchant_type_for_entitlement':
    "This merchant type isn't eligible for your current order benefits. Please select a different restaurant.",
  'cash_payment_limit_exceeded':
    'The cash payment amount exceeds the allowed limit. Please reduce it or use another payment method.',
  'code_already_applied':
    "This code has already been applied to your order. You can't use it again.",
  'code_buy_x_get_y_quantity_min_not_met':
    'You need to add more items to meet the minimum quantity for this buy-X-get-Y promo code.',
  'code_expired': 'Sorry, this promo code has expired. Please try a different one.',
  'code_happy_hour_max_usage_reached':
    "You've reached the maximum usage for this happy hour code. Check back next time!",
  'code_invalid_subscription_plan':
    "This code isn't valid for your subscription plan. Please verify your plan details.",
  'code_invalid_subscription_validation':
    "We couldn't validate this code with your subscription. Please try again or contact support.",
  'code_invalid_subscription_window':
    "This code isn't available during your subscription window. Check the terms for valid times.",
  'code_invalid_weekly_usage':
    'This code has exceeded its weekly usage limit. Try again next week.',
  'code_max_code_usage_reached':
    "You've reached the maximum usage for this code. It's no longer available.",
  'code_max_promo_usage_reached':
    "You've hit the max number of promos allowed. Remove one to continue.",
  'code_missing_menu_category':
    "This code requires items from a specific menu category that's missing from your cart.",
  'code_missing_menu_item':
    "This code requires a specific menu item that's not in your cart. Please add it.",
  'code_no_amount_remaining': 'This code has no remaining value. Please use a different one.',
  'code_no_subscription_found':
    'No subscription was found for this code. Please check your account.',
  'code_not_available_for_brand':
    "This code isn't available for this brand. Try a different code or brand.",
  'code_not_available_for_diner':
    "This code isn't available for your account. It may be restricted to certain users.",
  'code_not_available_for_restaurant':
    "This code can't be used at this restaurant. Please choose another.",
  'code_not_available_in_market':
    "This code isn't available in your market area. Check for local offers.",
  'code_not_available_on_application':
    "This code isn't available on this app or platform. Try using it on the website.",
  'code_not_available_to_order_type':
    'This code is only for certain order types (like delivery or pickup). Please adjust your order.',
  'code_not_available_to_payment_type':
    "This code can't be used with your selected payment type. Try a different payment method.",
  'code_not_combinable':
    "This code can't be combined with others in your order. Remove conflicting codes.",
  'code_not_first_time_app_user':
    "This code is only for first-time app users. You're not eligible.",
  'code_not_first_time_at_restaurant':
    "This code is only for first-time orders at this restaurant. You're not eligible.",
  'code_not_first_time_diner':
    'This code is only for new diners. Welcome back, but try another code!',
  'code_not_found': "We couldn't find this promo code. Please check for typos and try again.",
  'code_not_started': "This code hasn't started yet. Check the valid dates and try later.",
  'code_one_per_user_reached': "This code is limited to one per user, and you've already used it.",
  'code_only_available_on_catering_order':
    'This code is only for catering orders. Please switch to a catering order.',
  'code_only_available_on_delivery_order':
    'This code is only for delivery orders. Please select delivery.',
  'code_only_available_to_campus_diner':
    'This code is only for campus diners. Verify your location.',
  'code_only_available_to_lapsed_diner':
    "This code is only for returning diners who've been away. You're not eligible yet.",
  'code_order_min_not_met':
    'Your order total is below the minimum required for this code. Add more items.',
  'code_unavailable': 'This code is currently unavailable. Please try another one.',
  'diner_allocations_invalid': 'Your diner allocations are invalid. Please review and update them.',
  'event_metadata_not_found':
    "We couldn't find the event details. Please try again or contact support.",
  'expense_code_format_invalid': 'The expense code format is invalid. Please enter it correctly.',
  'expense_code_length_exceeded': 'The expense code is too long. Please shorten it.',
  'expense_code_required': 'An expense code is required. Please enter one to proceed.',
  'expense_comments_required': 'Expense comments are required. Please add some details.',
  'fulfillment_type_not_permitted':
    "This fulfillment type isn't allowed for the event. Choose another option.",
  'invalid.default': 'Sorry, something is invalid. Please check your details and try again.',
  'invalid.payments': 'Your payment details are invalid. Please verify and update them.',
  'invalid.phone': 'The phone number you entered is invalid. Please enter a valid one.',
  'invalid.postal_code': 'The postal code is invalid. Please enter a correct one for your area.',
  'invalid.quantity': 'The quantity you entered is invalid. Please adjust it.',
  'invalid-alreadycancelled':
    'This order has already been cancelled. No further changes can be made.',
  'invalid-alreadydeleted': 'This item or order has already been deleted.',
  'invalid-alreadyfulfilled': "This order has already been fulfilled. We can't modify it now.",
  'invalid-alreadysubmitted': 'This group cart participant has already submitted their order.',
  'invalid-amountexceeded': 'Your budget has been exceeded. Please reduce your order total.',
  'invalid-amountnotmet': "The amount doesn't meet the requirements. Please adjust your order.",
  'invalid-anonymousfutureordersnotallowed':
    "Anonymous future orders aren't allowed. Please sign in to schedule.",
  'invalid-cannotcancelhold': "This future order can't be cancelled. Contact support for help.",
  'invalid-cartcheckedout': 'This cart has already been checked out. Start a new one if needed.',
  'invalid-cashtipnotallowed':
    "Cash tips aren't allowed for this order. Choose another tip option.",
  'invalid-categorymaximumexceeded':
    "You've exceeded the maximum for this category. Remove some items.",
  'invalid-categoryminimumnotmet':
    "You need to select more options for this menu item. It's missing required choices.",
  'invalid-cateringdisabled': 'Catering is disabled for this restaurant. Choose standard ordering.',
  'invalid-cateringinforequired':
    'Catering info is required. Please provide details like headcount.',
  'invalid-cateringnotoffered': "Catering isn't offered here. Try a different restaurant.",
  'invalid-codenotcombinable':
    "This code can't be combined with your current setup. Remove it or adjust.",
  'invalid-coupondisabled': 'This coupon is disabled. Please use a different one.',
  'invalid-couponinvalidforrestaurant': "This coupon isn't valid for this restaurant. Try another.",
  'invalid-couponminimumnotmet':
    "Your order doesn't meet the minimum for this coupon. Add more items.",
  'invalid-couponnotcombinable': "This item coupon can't be combined with others. Choose one.",
  'invalid-couponnotfound': "We couldn't find this coupon. Please check and try again.",
  'invalid-enterpickupinfo': 'Please enter your pickup information to continue.',
  'invalid-freegrubnotfound': "We couldn't find your free Grub reward. Please verify your account.",
  'invalid-fulfillmentinforequired':
    'Fulfillment info is required. Please add details like address or time.',
  'invalid-futureorder-aftercutofftime':
    "This future order is past the cutoff time. We can't schedule it now.",
  'invalid-futureorder-menuitemunavailable':
    "This menu item isn't available for future orders. Remove or replace it.",
  'invalid-futureorder-noteditable': "This future order is past the cutoff and can't be edited.",
  'invalid-futureorderdisabled':
    'Future orders are disabled for this restaurant. Order for now instead.',
  'invalid-geocheckout':
    "There's an issue with your location during checkout. Please verify your address.",
  'invalid-groupalreadycheckedout': 'This group order has already been closed and checked out.',
  'invalid-groupcheckoutstate':
    'The group checkout state is invalid. Please refresh and try again.',
  'invalid-grouporderbudgetexceeded':
    'The group order has exceeded the budget. Adjust items to fit.',
  'invalid-invalidfreegrub': 'Your free Grub reward is invalid. Please check your eligibility.',
  'invalid-itemcouponminimumnotmet':
    "This item coupon requires a minimum that's not met. Add more.",
  'invalid-itemcouponnotcombinable':
    "This item coupon can't be combined. Remove conflicting items.",
  'invalid-itemnotcombinablewithcoupon':
    "This item can't be combined with your coupon. Remove one.",
  'invalid-length.delivery_instructions':
    'Your delivery instructions are too long. Please shorten them.',
  'invalid-length.special_instructions':
    'Your special instructions are too long. Please keep them brief.',
  'invalid-linepricedchanged': 'The price of a line item has changed. Please review your cart.',
  'invalid-maxcartslimitreached':
    "You've reached the maximum number of carts allowed. Close some first.",
  'invalid-maxcheckedoutcartslimitreached':
    "You've reached the limit for checked-out carts. Manage your orders.",
  'invalid-maximumexceeded': "You've exceeded the maximum allowed. Please reduce quantities.",
  'invalid-maximumpaymentsexceeded':
    "You've exceeded the maximum number of payments. Use fewer methods.",
  'invalid-menuitemnotfound': "We couldn't find this menu item. It may have been removed.",
  'invalid-menuitemunavailable': 'This menu item is unavailable right now. Please choose another.',
  'invalid-minimumnotmet.estimated_attendees':
    "The estimated attendees don't meet the minimum. Please update.",
  'invalid-negativetipnotallowed':
    "Negative tips aren't allowed. Please enter a positive amount or zero.",
  'invalid-nolongercancellable':
    "This future order can no longer be cancelled. It's too close to the time.",
  'invalid-nonzerocashtipnotallowed':
    "Non-zero cash tips aren't allowed for this order. Set to zero or change.",
  'invalid-notaccepted': "This payment type isn't accepted. Please choose another.",
  'invalid-notavailablefordelivery':
    "This restaurant isn't available for delivery. Try pickup instead.",
  'invalid-notavailablefordeliveryatdinerlocation':
    "This restaurant doesn't deliver to your address. Change address or restaurant.",
  'invalid-notavailableforfuturedelivery':
    "Future delivery isn't available here. Order for now or choose pickup.",
  'invalid-notavailableforfuturepickup':
    "Future pickup isn't available. Schedule for today instead.",
  'invalid-notavailableforpickup': "This restaurant isn't available for pickup. Try delivery.",
  'invalid-notavailablefromkendo':
    "This isn't available from Kendo. Please try a different option.",
  'invalid-notenoughfreegrub':
    "You don't have enough free Grub rewards for this. Earn more or adjust.",
  'invalid-notopen':
    "The restaurant is closed, so we can't modify menu items. Try when they're open.",
  'invalid-olorestaurant': 'This is an invalid OLO restaurant. Please select another.',
  'invalid-onlyonerestaurantallowed': 'Only one restaurant is allowed per order. Remove extras.',
  'invalid-optionnotfound': "This menu item option wasn't found. It may be unavailable.",
  'invalid-ordermaximumexceeded': 'Your order has exceeded the maximum allowed. Reduce items.',
  'invalid-orderminimumnotmet': "Your order doesn't meet the minimum total. Add more items.",
  'invalid-posresponse': 'There was an invalid response from the point-of-sale system. Try again.',
  'invalid-quantitylimitperitempercartexceeded':
    "You've exceeded the quantity limit per item in this cart. Reduce it.",
  'invalid-required.default': 'A required field is missing. Please fill it in.',
  'invalid-required.fulfillment_info.delivery_info.cross_streets':
    'Cross streets are required for delivery. Please add them.',
  'invalid-sharedcartdisabled': 'Shared carts are disabled. Please use individual ordering.',
  'invalid-shorttoextendedfutureordernotallowed':
    "Switching from short to extended future orders isn't allowed. Reschedule carefully.",
  'invalid-supervisorname': 'The supervisor name is invalid. Please enter a valid one.',
  'invalid-timecutofffordelivery':
    "You've passed the cutoff time for delivery today. Try tomorrow.",
  'invalid-timecutoffforpickup': "You've passed the cutoff time for pickup. Schedule for later.",
  'invalid-tipminimumnotmet': "Your tip doesn't meet the minimum required. Please increase it.",
  'invalid-twoindeterminatepayments':
    "You can't have two indeterminate payments. Adjust your payment methods.",
  'invalid-unusable': 'This is unusable right now. Please try a different option.',
  'invalid-whenforpastmaxallowedtime.when_for':
    'The selected time is past the maximum allowed. Choose an earlier time.',
  'invalid-zerodollarsallocation':
    "Zero-dollar allocations aren't allowed. Please assign a positive amount.",
  'invalid_code': 'This code is invalid. Please check and try again.',
  'invalid_entitlement_for_merchant':
    "Your entitlements aren't valid for this merchant. Select another.",
  'invalid_event_for_diner': "This event isn't valid for your account. Please verify.",
  'non_combinable_entitlement_type':
    "Cash and promo entitlements can't be combined. Choose one type.",
  'not_found.payment_id': "The fulfillment type wasn't found. Please select a valid one.",
  'null-supervisorname': "Supervisor name can't be null. Please provide one.",
  'order_outside_schedule':
    'Your order is outside the scheduled hours. Please choose a time within availability.',
  'payments.unprocessable': "We couldn't process your payment. Please try a different method.",
  'paypal_generic': "There's an issue with PayPal. Please reset your payment and try again.",
  'predefined_reason_invalid': 'The predefined reason is invalid. Please select a valid one.',
  'predefined_reason_required': 'A predefined reason is required. Please choose one.',
  'reward_not_available_for_diner':
    "This reward isn't available for your account. Check eligibility.",
  'subscription_purchased_on_future_order_error':
    "Subscriptions can't be purchased on future orders. Buy now instead.",
  'unified_reward_order_min_not_met':
    "Your order doesn't meet the minimum for this unified reward. Add more.",
  'unknown': 'An unknown error occurred. Please try again or contact support.',
  'unprocessable': "We couldn't process this payment. Verify your details.",
  'invalid-notavailableforfuturedeliveryduetotier':
    "Future delivery isn't available due to your tier. Upgrade or choose pickup.",
  'invalid-notavailableforfuturepickupduetotier':
    "Future pickup isn't available due to your tier. Try delivery instead.",
  'invalid-notavailablefordeliveryduetotier':
    "Delivery isn't available due to your tier. Consider upgrading.",
  'invalid-notavailableforpickupduetotier':
    "Pickup isn't available due to your tier. Try delivery or upgrade.",
}
